import React, { useRef, useEffect, useState } from 'react';
import useSWR, { mutate } from 'swr';
import { useParams } from 'react-router-dom';
import {
  Form,
  Row,
  Col,
  Button,
  Alert,
  Card,
  Modal,
  Spinner,
} from 'react-bootstrap';
import { toast } from 'react-toastify';
import { FaWhatsapp } from 'react-icons/fa';
import api from '../../../services/api';
import history from '../../../services/history';
import generateEcoCongenitoPDF from './generateEcoCongenitoPDF';
import CustomFileInput from '../../../components/CustomFileInput';
import useUploadFiles from '../../../hooks/useUploadFiles';
import ModalEco from './ModalEco';
import LaudoCardHeader from '../../../components/laudos/LaudoCardHeader';
import selectEcoById from '../../../services/ecodoppler';
import styles from '../../../styles.module.css';
import generateDopplerTextReport from './generateDopplerTextReport';
import mapEcoFormFields from './mapEcoFormFields';
import mapEcoCongenitoFields from './mapEcoCongenitoFields';

// Função para normalizar campos float
function normalizeFloatFields(data, floatFields) {
  const result = { ...data };
  floatFields.forEach(field => {
    if (typeof result[field] === 'string') {
      const clean = result[field].replace(',', '.');
      result[field] = clean === '' ? null : parseFloat(clean);
    }
  });
  return result;
}

const floatFields = [
  'peso',
  'altura',
  'superficiecorporea',
  'septo',
  'ppve',
  'dd',
  'ds',
  'fraejeBiplanar',
  'fe3d',
  'tempoEjecaoVE',
  'tempoAEMitral',
  'indiceCardiaco',
  'diamAE',
  'indiceVolAE',
  'diamAO',
  'diamSaidaVE',
  'diamAnelPulmonar',
  'diametroVSVD',
  'gradienteVEVD',
  'vtiPulmonar',
  'vtiVSVE',
  'areaMitral',
  'gradienteMitral',
  'pisaMitral',
  'volumeRegMitral',
  'areaAortica',
  'pisaAortica',
  'gmvao',
  'gmvsve',
  'gmvTric',
  'gpit',
  'gradientePulm',
  'gradMitral',
  'gradAortica',
  'gradTricuspide',
  'gradPulmonar',
];

const DeleteButton = ({ field, onDelete }) => (
  <Button
    variant="outline-danger"
    size="sm"
    style={{ marginLeft: 4, padding: '0 6px', height: 30 }}
    onClick={() => onDelete(field)}
    tabIndex={-1}
  >
    ×
  </Button>
);

// as linhas comentadas significam que estamos pulando a sequencia
// nao excluir dessa sequencia
const ecoSequence = [
  // 'tipoLaudo',
  'sexo',
  'idade',
  'peso',
  'altura',
  'superficiecorporea',
  'pressaosistolica',
  'frequenciacardiaca',
  'ecocardiograma',
  'septo',
  'ppve',
  'dd',
  'ds',
  'fraejeBiplanar',
  // 'fe3d',
  'achadosDescritivos',
  'contracaoSegmentar',
  'regiaoVE',
  // 'tempoEjecaoVE',
  // 'tempoAEMitral',
  // 'indiceCardiaco',
  'comentariosDiast',
  'diamAE',
  'indiceVolAE',
  'atrioEsq',
  'valvaMitral',
  'diamAO',
  // 'diamSaidaVE',
  'valvaAortica',
  'aorta',
  'atrioDireito',
  'veiaCavaInferior',
  'ventriculoDireito',
  'valvaTricuspide',
  'valvaPulmonar',
  // 'diamAnelPulmonar',
  'pericardio',
  // Estudo Congênito
  'situs',
  'septoIA',
  'medidaCIA',
  'septoIV',
  'regiaoSeptoIV',
  'medidaCIV',
  'diametroVSVD',
  'gradienteVEVD',
  'vtiPulmonar',
  'vtiVSVE',
  'concordAV',
  'viaSaida',
  'concordVA',
  'arteriaPulmonar',
  'arteriaCoronaria',
  'arcoAortico',
  'conexaoVenoAtrial',
  'estruturasAnomalas',
  'areaMitral',
  'gradienteMitral',
  'refluxoMitral',
  'pisaMitral',
  'volumeRegMitral',
  'areaAortica',
  'pisaAortica',
  'refluxoAortica',
  'gmvao',
  'gmvsve',
  'gmvTric',
  'gpit',
  'refluxoTric',
  'gradientePulm',
  'refluxoPulm',
  'gradMitral',
  'refluxoProtMitral',
  'intensidadeProtMitral',
  'gradAortica',
  'refluxoProtAortica',
  'intensidadeProtAortica',
  'gradTricuspide',
  'refluxoProtTricuspide',
  'intensidadeProtTricuspide',
  'gradPulmonar',
  'refluxoProtPulmonar',
  'intensidadeProtPulmonar',
  'valvulopatia',
  'intensidadeValv',
  'predominanciaValv',
  'protese',
  'descProtese',
  'predominanciaProtese',
  'conSeg',
  'regiao',
  'conclusoes',
  'intensidadeConclusoes',
  'observacoes',
];

const editStyle = {
  display: 'block',
  fontSize: '1.0rem',
  backgroundColor: '#d9fbda',
  border: '2px solid #016722',
  borderRadius: '5px',
  color: '#000000',
  fontWeight: 500,
  fontFamily: 'Segoe UI, sans-serif',
  marginBottom: '2px',
  padding: '2px 6px',
};

const arrayStyle = {
  width: '50px',
  fontSize: '1.1rem',
  fontWeight: 800,
  fontFamily: 'Segoe UI, sans-serif',
  display: 'block',
  backgroundColor: '#e0f2fe',
  border: '1px solid #60a5fa',
  borderRadius: '5px',
  color: '#1e3a8a',
  marginBottom: '2px',
  padding: '2px 6px',
};

export default function FormEcoCongenito() {
  const { id } = useParams();
  const { data: eco, error, isLoading } = useSWR(id ? ['eco', id] : null, () =>
    selectEcoById(id)
  );
  const [showModal, setShowModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    files,
    setFiles,
    showUpload,
    setShowUpload,
    isUploading,
    confirmUpload,
    handleCloseModal,
  } = useUploadFiles({ atividade: eco });
  const peso = parseFloat(eco?.PESOPAC) || 0;
  const altura = parseFloat(eco?.ALTURAPAC) || 0;
  const superficieCorporea =
    peso && altura
      ? (0.007184 * (altura * 100) ** 0.725 * peso ** 0.425).toFixed(2)
      : '';
  const [formData, setFormData] = useState({
    codEco: eco?.CODECO || '',
    tipoLaudo: eco?.TIPOLAUDO || 'Alterado', // Normal/Alterado
    tipoEco: eco?.TIPOECO || '', // EcoC/EcoD
    sexo: eco?.Sexo || '',
    idade: eco?.idadePAC || '',
    peso: '',
    altura: '',
    superficiecorporea: '',
    pressaosistolica: '',
    frequenciacardiaca: '',
    // I - Ecocardiograma
    ecocardiograma: Array(10).fill(''),
    // II - Diâmetros e Função Sistólica do VE
    septo: '',
    ppve: '',
    dd: '',
    ds: '',
    fraejeBiplanar: '',
    fe3d: '',
    achadosDescritivos: Array(10).fill(''),
    contracaoSegmentar: Array(10).fill(''),
    regiaoVE: Array(10).fill(''),
    // III - Avaliação da Função Diastólica do VE
    tempoEjecaoVE: '',
    tempoAEMitral: '',
    indiceCardiaco: '',
    comentariosDiast: Array(10).fill(''),
    // IV - Átrio Esquerdo e Valva Mitral
    diamAE: '',
    indiceVolAE: '',
    atrioEsq: Array(5).fill(''),
    valvaMitral: Array(5).fill(''),
    // V - Valva Aórtica e Aorta
    diamAO: '',
    diamSaidaVE: '',
    valvaAortica: Array(5).fill(''),
    aorta: Array(5).fill(''),
    // VI - Cavidades Direita e Valvas
    atrioDireito: Array(5).fill(''),
    veiaCavaInferior: Array(5).fill(''),
    ventriculoDireito: Array(5).fill(''),
    valvaTricuspide: Array(5).fill(''),
    valvaPulmonar: Array(5).fill(''),
    diamAnelPulmonar: '',
    // VII - Pericárdio
    pericardio: Array(10).fill(''),
    // Estudo Congênito
    situs: Array(5).fill(''),
    septoIA: Array(5).fill(''),
    medidaCIA: Array(5).fill(''),
    septoIV: Array(5).fill(''),
    regiaoSeptoIV: Array(5).fill(''),
    medidaCIV: Array(5).fill(''),
    diametroVSVD: '',
    gradienteVEVD: '',
    vtiPulmonar: '',
    vtiVSVE: '',
    concordAV: Array(5).fill(''),
    viaSaida: Array(5).fill(''),
    concordVA: Array(5).fill(''),
    arteriaPulmonar: Array(5).fill(''),
    arteriaCoronaria: Array(5).fill(''),
    arcoAortico: Array(5).fill(''),
    conexaoVenoAtrial: Array(5).fill(''),
    estruturasAnomalas: Array(5).fill(''),
    // VIII - Valvulopatia da Valva Mitral
    areaMitral: '',
    gradienteMitral: '',
    refluxoMitral: Array(5).fill(''),
    pisaMitral: '',
    volumeRegMitral: '',
    // IX - Valvulopatia da Valva Aórtica
    areaAortica: '',
    pisaAortica: '',
    gmvao: '',
    gmvsve: '',
    refluxoAortica: Array(5).fill(''),
    // X - Valvulopatia de Valva Tricúspide/Pulmonar
    gmvTric: '',
    gpit: '',
    refluxoTric: Array(5).fill(''),
    refluxoPulm: Array(5).fill(''),
    gradientePulm: '',
    // XI - Próteses
    gradMitral: '',
    refluxoProtMitral: Array(5).fill(''),
    intensidadeProtMitral: Array(5).fill(''),
    gradAortica: '',
    refluxoProtAortica: Array(5).fill(''),
    intensidadeProtAortica: Array(5).fill(''),
    gradTricuspide: '',
    refluxoProtTricuspide: Array(5).fill(''),
    intensidadeProtTricuspide: Array(5).fill(''),
    gradPulmonar: '',
    refluxoProtPulmonar: Array(5).fill(''),
    intensidadeProtPulmonar: Array(5).fill(''),
    // XII - Conclusões
    valvulopatia: Array(5).fill(''),
    intensidadeValv: Array(5).fill(''),
    predominanciaValv: Array(5).fill(''),
    protese: Array(5).fill(''),
    descProtese: Array(5).fill(''),
    predominanciaProtese: Array(5).fill(''),
    conSeg: Array(5).fill(''),
    regiao: Array(5).fill(''),
    conclusoes: Array(10).fill(''),
    intensidadeConclusoes: Array(10).fill(''),
    // XIII - Observações
    observacoes: '',
  });

  // Cálculos de Congênito em tempo real
  const dvsvdNum =
    parseFloat(String(formData.diametroVSVD).replace(',', '.')) || 0;
  const dvsveNum =
    parseFloat(String(formData.diamSaidaVE).replace(',', '.')) || 0;
  const vtipNum =
    parseFloat(String(formData.vtiPulmonar).replace(',', '.')) || 0;
  const vtivsveNum =
    parseFloat(String(formData.vtiVSVE).replace(',', '.')) || 0;
  const psNum =
    parseFloat(String(formData.pressaosistolica).replace(',', '.')) || 0;
  const gvevdNum =
    parseFloat(String(formData.gradienteVEVD).replace(',', '.')) || 0;

  const fluxoPulmonar = (0.785 * dvsvdNum * dvsvdNum * vtipNum).toFixed(1);
  const fluxoSistemico = (0.785 * dvsveNum * dvsveNum * vtivsveNum).toFixed(1);
  const relacaoPS =
    parseFloat(fluxoSistemico) > 0 && parseFloat(fluxoPulmonar) > 0
      ? (parseFloat(fluxoPulmonar) / parseFloat(fluxoSistemico)).toFixed(1)
      : '0.0';
  const estimativaPSVD =
    gvevdNum > 0 && psNum > 0 ? (psNum - gvevdNum).toFixed(1) : '';

  // Carrega o laudo aberto. O estudo congênito não entra na cópia.
  useEffect(() => {
    if (eco) {
      setFormData(prev => ({
        ...prev,
        ...mapEcoFormFields(eco),
        ...mapEcoCongenitoFields(eco),
        codEco: eco.CODECO || '',
        idade: eco.idadePAC || '',
        superficiecorporea: superficieCorporea || eco?.SUPCORP,
      }));
    }
  }, [eco]);

  const ecoArrayRefs = useRef({});
  const ecoRefs = useRef({});

  useEffect(() => {
    Object.keys(formData).forEach(key => {
      if (Array.isArray(formData[key])) {
        if (!ecoArrayRefs.current[key]) {
          ecoArrayRefs.current[key] = [];
        }
        formData[key].forEach((_, i) => {
          if (!ecoArrayRefs.current[key][i]) {
            ecoArrayRefs.current[key][i] = React.createRef();
          }
        });
      } else {
        if (!ecoRefs.current[key]) {
          ecoRefs.current[key] = React.createRef();
        }
      }
    });
  }, [formData]);

  const handleEcoKeyDown = (e, key) => {
    if (e.key === 'Enter') {
      const currentIndex = ecoSequence.indexOf(key);
      const nextKey = ecoSequence[currentIndex + 1];

      if (nextKey) {
        const nextRef = Array.isArray(formData[nextKey])
          ? ecoArrayRefs.current[nextKey]?.[0]
          : ecoRefs.current[nextKey];

        if (nextRef?.current) {
          nextRef.current.focus();
          e.preventDefault();
        }
      }
    }
  };

  const handleEcoArrayKeyDown = (e, key, index) => {
    if (e.key === 'ArrowRight' && index < formData[key].length - 1) {
      ecoArrayRefs.current[key][index + 1]?.current?.focus();
    } else if (e.key === 'ArrowLeft' && index > 0) {
      ecoArrayRefs.current[key][index - 1]?.current?.focus();
    } else if (e.key === 'Enter' || e.key === 'ArrowDown') {
      const currentGroupIndex = ecoSequence.indexOf(key);
      const nextKey = ecoSequence[currentGroupIndex + 1];

      const nextRef = Array.isArray(formData[nextKey])
        ? ecoArrayRefs.current[nextKey]?.[0]
        : ecoRefs.current[nextKey];

      if (nextRef?.current) {
        nextRef.current.focus();
        e.preventDefault();
      }
    }
  };

  const handleArrayChange = (e, field, index) => {
    const { value } = e.target;
    // Permite vazio ou apenas números
    if (value === '' || /^\d+$/.test(value)) {
      setFormData(prev => {
        const newArray = [...prev[field]];
        newArray[index] = value;
        return {
          ...prev,
          [field]: newArray,
        };
      });
    } else {
      toast.info('Digite apenas números neste campo.');
    }
  };

  const handleFloatArrayChange = (e, field, index) => {
    const { value } = e.target;
    if (value === '' || /^[\d.,]+$/.test(value)) {
      setFormData(prev => {
        const newArray = [...prev[field]];
        newArray[index] = value;
        return {
          ...prev,
          [field]: newArray,
        };
      });
    } else {
      toast.info('Digite apenas valores numéricos neste campo.');
    }
  };

  const handleChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleClearArrayAndDelete = tipo => {
    const { codEco } = formData;

    setFormData(prev => ({
      ...prev,
      [tipo]: Array(prev[tipo].length).fill(''),
    }));

    if (codEco) {
      mutate(['eco', codEco], undefined, { revalidate: true });
    }
    toast.info(
      'Atenção: é necessário gravar o laudo para salvar as alterações feitas nesta tabela.'
    );
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const normalizedData = normalizeFloatFields(formData, floatFields);
      await api.put('/eco-details', normalizedData);
      toast.success('Dados salvos com sucesso!');
      if (id) {
        await mutate(['eco', id]);
      }
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Erro ao salvar dados');
    } finally {
      setIsSubmitting(false);
      scrollTo(0, 0);
    }
  };

  if (error) {
    return (
      <Alert variant="danger" style={{ marginTop: 10 }}>
        <Alert.Heading>Erro ao carregar laudo de eco!</Alert.Heading>
        <p>Tente novamente.</p>
      </Alert>
    );
  }

  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '80vh',
        }}
      >
        <Spinner animation="border" variant="dark" size="lg" />
      </div>
    );
  }

  // TODO: fazer componentes de todos esses inputs repetitivos! ou hookform?
  return (
    <>
      <Card className="mt-2">
        <LaudoCardHeader
          title={`ECOCARDIOGRAMA CONGÊNITO - CODIGO: ${eco?.CODECO || ''}`}
        />
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '8px 16px 0 16px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span
              style={{
                fontSize: '1.2rem',
                fontWeight: '900',
                color: '#dc2626',
                backgroundColor: '#fee2e2',
                padding: '3px 12px',
                borderRadius: '6px',
                border: '2px solid #ef4444',
                letterSpacing: '1px',
                textTransform: 'uppercase',
                display: 'inline-block',
              }}
            >
              Congênito
            </span>
          </div>
          {/* Grupo do Tipo Eco (Alinhado à esquerda/centro) */}
          <div className="d-flex align-items-center" style={{ gap: '12px' }}>
            <Form.Label className="mb-0 font-weight-bold" style={{ whiteSpace: 'nowrap' }}>
              Tipo Eco:
            </Form.Label>

            <Form.Check
              inline
              type="radio"
              id="tipoEco-doppler"
              name="tipoEco"
              label="Doppler"
              value="EcoD"
              checked={formData.tipoEco === 'EcoD'}
              onChange={handleChange}
              size="sm"
              className="mb-0"
              ref={ecoRefs.current['tipoEco']}
              onKeyDown={e => handleEcoKeyDown(e, 'tipoEco')}
            />

            <Form.Check
              inline
              type="radio"
              id="tipoEco-congenito"
              name="tipoEco"
              label="Congênito"
              value="EcoC"
              checked={formData.tipoEco === 'EcoC'}
              onChange={handleChange}
              size="sm"
              className="mb-0"
              ref={ecoRefs.current['tipoEco']}
              onKeyDown={e => handleEcoKeyDown(e, 'tipoEco')}
            />
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => {
                if (eco?.CODREALIZOU === 4860) {
                  history.push(`/espelhoCongTxt/${eco?.CODECO}`);
                } else {
                  history.push(`/espelhoCong/${eco?.CODECO}`);
                }
              }}
            >
              Espelho
            </Button>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => {
                history.push(`/reportEcoCongenito/${eco?.CODECO}`);
              }}
            >
              Visualizar Laudo
            </Button>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => setShowModal(true)}
              style={{ display: 'flex', alignItems: 'center' }}
            >
              <span style={{ marginRight: 6 }}>
                <svg
                  width="1em"
                  height="1em"
                  viewBox="0 0 16 16"
                  fill="currentColor"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M10 1.5A1.5 1.5 0 0 1 11.5 3v1h-7V3A1.5 1.5 0 0 1 6 1.5h4zm1.5 2A.5.5 0 0 0 11 3V1.5A2.5 2.5 0 0 0 8.5-1h-4A2.5 2.5 0 0 0 2 1.5V3a.5.5 0 0 0-.5.5v10A2.5 2.5 0 0 0 4 16h8a2.5 2.5 0 0 0 2.5-2.5v-10A.5.5 0 0 0 14 3V1.5A1.5 1.5 0 0 0 12.5 0h-4A1.5 1.5 0 0 0 7 1.5V3a.5.5 0 0 0-.5.5v10A1.5 1.5 0 0 0 8 15h4a1.5 1.5 0 0 0 1.5-1.5v-10z" />
                </svg>
              </span>
              Copiar Laudo Anterior
            </Button>
          </div>
        </div>
        <Card.Body>
          <Form onSubmit={handleSubmit}>
            <Row className="mb-3">
              <Col>
                Nome
                <Form.Control name="nome" value={eco?.NOMEPAC || ''} readOnly />
              </Col>
              <Col>
                Data de Nascimento
                <Form.Control
                  name="dataNascimento"
                  value={eco?.dataNasc || ''}
                  readOnly
                />
              </Col>
              <Col>
                Solicitante
                <Form.Control
                  name="nomeSolicitante"
                  value={eco?.Solicitou || ''}
                  readOnly
                />
              </Col>
              <Col>
                Convênio
                <Form.Control
                  name="siglaConvenio"
                  value={eco?.SIGLACONV || ''}
                  readOnly
                />
              </Col>
            </Row>

            <Row>
              <Col>
                <Form.Group as={Row} className="mb-0">
                  <Form.Label column sm={2}>
                    Sexo:
                  </Form.Label>
                  <Col sm={4}>
                    <Form.Control
                      name="sexo"
                      value={formData.sexo}
                      onChange={handleChange}
                      size="sm"
                      ref={ecoRefs.current['sexo']}
                      onKeyDown={e => handleEcoKeyDown(e, 'sexo')}
                      readOnly
                    />
                  </Col>
                </Form.Group>

                <Form.Group as={Row} className="mb-0">
                  <Form.Label column sm={2}>
                    Idade:
                  </Form.Label>
                  <Col sm={4}>
                    <Form.Control
                      name="idade"
                      value={formData.idade}
                      onChange={handleChange}
                      size="sm"
                      ref={ecoRefs.current['idade']}
                      onKeyDown={e => handleEcoKeyDown(e, 'idade')}
                      readOnly
                    />
                  </Col>
                </Form.Group>
                <Form.Group as={Row} className="mb-0">
                  <Form.Label column sm={2}>
                    Peso (Kg):
                  </Form.Label>
                  <Col sm={4}>
                    <Form.Control
                      name="peso"
                      value={formData.peso}
                      onChange={handleChange}
                      type="number"
                      size="sm"
                      ref={ecoRefs.current['peso']}
                      onKeyDown={e => handleEcoKeyDown(e, 'peso')}
                      style={editStyle}
                    />
                  </Col>
                </Form.Group>

                <Form.Group as={Row} className="mb-0">
                  <Form.Label column sm={2}>
                    Altura (m):
                  </Form.Label>
                  <Col sm={4}>
                    <Form.Control
                      name="altura"
                      style={editStyle}
                      value={formData.altura}
                      onChange={handleChange}
                      type="number"
                      size="sm"
                      ref={ecoRefs.current['altura']}
                      onKeyDown={e => handleEcoKeyDown(e, 'altura')}
                    />
                  </Col>
                </Form.Group>
              </Col>
              <Col>
                <Form.Group as={Row} className="mb-0">
                  <Form.Label column sm={4}>
                    Superfície Corpórea (m2):
                  </Form.Label>
                  <Col sm={4}>
                    <Form.Control
                      name="superficiecorporea"
                      style={editStyle}
                      value={formData.superficiecorporea}
                      onChange={handleChange}
                      type="number"
                      size="sm"
                      ref={ecoRefs.current['superficiecorporea']}
                      onKeyDown={e => handleEcoKeyDown(e, 'superficiecorporea')}
                    />
                  </Col>
                </Form.Group>

                <Form.Group as={Row} className="mb-0">
                  <Form.Label column sm={4}>
                    Pressão Sistólica (mmHg):
                  </Form.Label>
                  <Col sm={4}>
                    <Form.Control
                      name="pressaosistolica"
                      style={editStyle}
                      value={formData.pressaosistolica}
                      onChange={handleChange}
                      size="sm"
                      ref={ecoRefs.current['pressaosistolica']}
                      onKeyDown={e => handleEcoKeyDown(e, 'pressaosistolica')}
                    />
                  </Col>
                </Form.Group>
                <Form.Group as={Row} className="mb-0">
                  <Form.Label column sm={4}>
                    Frequência Cardíaca:
                  </Form.Label>
                  <Col sm={4}>
                    <Form.Control
                      name="frequenciacardiaca"
                      style={editStyle}
                      value={formData.frequenciacardiaca}
                      onChange={handleChange}
                      size="sm"
                      ref={ecoRefs.current['frequenciacardiaca']}
                      onKeyDown={e => handleEcoKeyDown(e, 'frequenciacardiaca')}
                    />
                  </Col>
                </Form.Group>
                <Form.Group as={Row} className="mb-0">
                  <Form.Label column sm={4}>
                    Tipo Laudo:
                  </Form.Label>
                  <Col sm={2}>
                    <Form.Check
                      name="tipoLaudo"
                      className={`m-2`}
                      type="radio"
                      label="Normal"
                      value="Normal"
                      checked={formData.tipoLaudo === 'Normal'}
                      onChange={handleChange}
                      size="sm"
                      ref={ecoRefs.current['tipoLaudo']}
                      onKeyDown={e => handleEcoKeyDown(e, 'tipoLaudo')}
                    />
                  </Col>

                  <Col sm={2}>
                    <Form.Check
                      name="tipoLaudo"
                      className="m-2"
                      type="radio"
                      label="Alterado"
                      value="Alterado"
                      checked={formData.tipoLaudo === 'Alterado'}
                      onChange={handleChange}
                      size="sm"
                      ref={ecoRefs.current['tipoLaudo']}
                      onKeyDown={e => handleEcoKeyDown(e, 'tipoLaudo')}
                    />
                  </Col>
                </Form.Group>
              </Col>
            </Row>
            <h5>I - Ecocardiograma</h5>
            <Row className="align-items-end mb-3">
              <Col xs="auto">
                <Form.Label>Ecocardiograma</Form.Label>
                <div className="d-flex flex-wrap gap-1">
                  {formData.ecocardiograma.map((val, i) => (
                    <Form.Control
                      // ref={ecoArrayRefs.current['valvaMitral']?.[index]}
                      key={i}
                      ref={ecoArrayRefs.current.ecocardiograma?.[i]}
                      size="sm"
                      style={arrayStyle}
                      value={val ?? ''}
                      onChange={e => handleArrayChange(e, 'ecocardiograma', i)}
                      onKeyDown={e =>
                        handleEcoArrayKeyDown(e, 'ecocardiograma', i)
                      }
                    />
                  ))}
                  <DeleteButton
                    field="ecocardiograma"
                    onDelete={handleClearArrayAndDelete}
                  />
                </div>{' '}
                {eco?.obs?.map(o => (
                  <span key={o.CODOBS} className={styles.spanStyle}>
                    [ {o.CODOBS} — {o.DESCRICAO || 'Descrição não encontrada'} ]
                  </span>
                ))}
              </Col>
            </Row>

            <h5>II - Diâmetros e Função Sistólica do VE</h5>
            <Form.Group>
              <Row>
                <Col>
                  <Form.Label>Septo</Form.Label>
                  <Form.Control
                    name="septo"
                    style={editStyle}
                    type="number"
                    size="sm"
                    ref={ecoRefs.current['septo']}
                    value={formData.septo}
                    onChange={handleChange}
                    onKeyDown={e => handleEcoKeyDown(e, 'septo')}
                  />
                </Col>
                <Col>
                  <Form.Label>PP VE</Form.Label>
                  <Form.Control
                    name="ppve"
                    style={editStyle}
                    type="number"
                    size="sm"
                    value={formData.ppve}
                    ref={ecoRefs.current['ppve']}
                    onChange={handleChange}
                    onKeyDown={e => handleEcoKeyDown(e, 'ppve')}
                  />
                </Col>
                <Col>
                  <Form.Label>DD (cm)</Form.Label>
                  <Form.Control
                    name="dd"
                    style={editStyle}
                    type="number"
                    size="sm"
                    value={formData.dd}
                    ref={ecoRefs.current['dd']}
                    onChange={handleChange}
                    onKeyDown={e => handleEcoKeyDown(e, 'dd')}
                  />
                </Col>
                <Col>
                  <Form.Label>DS (cm)</Form.Label>
                  <Form.Control
                    name="ds"
                    style={editStyle}
                    type="number"
                    size="sm"
                    value={formData.ds}
                    ref={ecoRefs.current['ds']}
                    onChange={handleChange}
                    onKeyDown={e => handleEcoKeyDown(e, 'ds')}
                  />
                </Col>
                <Col>
                  <Form.Label>Fraç Ej (Biplanar)</Form.Label>
                  <Form.Control
                    name="fraejeBiplanar"
                    style={editStyle}
                    type="number"
                    size="sm"
                    value={formData.fraejeBiplanar}
                    ref={ecoRefs.current['fraejeBiplanar']}
                    onChange={handleChange}
                    onKeyDown={e => handleEcoKeyDown(e, 'fraejeBiplanar')}
                  />
                </Col>
                <Col>
                  <Form.Label>FE (3D)</Form.Label>
                  <Form.Control
                    name="fe3d"
                    style={editStyle}
                    type="number"
                    size="sm"
                    // ref={ecoRefs.current[0]}
                    value={formData.fe3d}
                    onChange={handleChange}
                  />
                </Col>
              </Row>
            </Form.Group>
            <Form.Group>
              <Row className="align-items-end mb-3">
                <Col xs="auto">
                  <Form.Label>Achados Descritivos</Form.Label>
                  <div className="d-flex flex-wrap gap-1">
                    {formData.achadosDescritivos.map((val, i) => (
                      <Form.Control
                        key={i}
                        ref={ecoArrayRefs.current.achadosDescritivos?.[i]}
                        size="sm"
                        style={arrayStyle}
                        value={val ?? ''}
                        onChange={e =>
                          handleArrayChange(e, 'achadosDescritivos', i)
                        }
                        onKeyDown={e =>
                          handleEcoArrayKeyDown(e, 'achadosDescritivos', i)
                        }
                      />
                    ))}
                    <DeleteButton
                      field="achadosDescritivos"
                      onDelete={handleClearArrayAndDelete}
                    />
                  </div>
                  {eco?.achados?.map((o, index) => (
                    <span key={index} className={styles.spanStyle}>
                      [ {o.CODACHAVE} —{' '}
                      {o.DESCRICAO || 'Descrição não encontrada'} ]
                    </span>
                  ))}
                </Col>
              </Row>
              <Row className="align-items-end mb-3">
                <Col xs="auto">
                  <Form.Label>Contração Segmentar</Form.Label>
                  <div className="d-flex flex-wrap gap-1">
                    {formData.contracaoSegmentar.map((val, i) => (
                      <Form.Control
                        key={i}
                        ref={ecoArrayRefs.current.contracaoSegmentar?.[i]}
                        size="sm"
                        style={arrayStyle}
                        value={val ?? ''}
                        onChange={e =>
                          handleArrayChange(e, 'contracaoSegmentar', i)
                        }
                        onKeyDown={e =>
                          handleEcoArrayKeyDown(e, 'contracaoSegmentar', i)
                        }
                      />
                    ))}
                    <DeleteButton
                      field="contracaoSegmentar"
                      onDelete={handleClearArrayAndDelete}
                    />
                  </div>
                  {eco?.contraRegiao?.map((o, index) => (
                    <span key={index} className={styles.spanStyle}>
                      [ {o.CODCONTRASEG} —{' '}
                      {o.CONTRACAO || 'Descrição não encontrada'} ]
                    </span>
                  ))}
                </Col>
              </Row>
              <Row className="align-items-end mb-3">
                <Col xs="auto">
                  <Form.Label>Região</Form.Label>
                  <div className="d-flex flex-wrap gap-1">
                    {formData.regiaoVE.map((val, i) => (
                      <Form.Control
                        key={i}
                        ref={ecoArrayRefs.current.regiaoVE?.[i]}
                        size="sm"
                        style={arrayStyle}
                        value={val ?? ''}
                        onChange={e => handleArrayChange(e, 'regiaoVE', i)}
                        onKeyDown={e => handleEcoArrayKeyDown(e, 'regiaoVE', i)}
                      />
                    ))}
                    <DeleteButton
                      field="regiaoVE"
                      onDelete={handleClearArrayAndDelete}
                    />
                  </div>
                  {eco?.contraRegiao?.map((o, index) => (
                    <span key={index} className={styles.spanStyle}>
                      [ {o.CODREGIAO} — {o.REGIAO || 'Descrição não encontrada'}{' '}
                      ]
                    </span>
                  ))}
                </Col>
              </Row>
            </Form.Group>

            <h5>III - Avaliação da Função Diastólica do VE</h5>
            <Form.Group>
              <Row>
                <Col>
                  <Form.Group>
                    <Form.Label>Tempo Ejeção VE</Form.Label>
                    <Form.Control
                      name="tempoEjecaoVE"
                      style={editStyle}
                      type="number"
                      ref={ecoRefs.current['tempoEjecaoVE']}
                      value={formData.tempoEjecaoVE}
                      onChange={handleChange}
                      onKeyDown={e => handleEcoKeyDown(e, 'tempoEjecaoVE')}
                    />
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group>
                    <Form.Label>Tempo AE Mitral</Form.Label>
                    <Form.Control
                      name="tempoAEMitral"
                      style={editStyle}
                      type="number"
                      ref={ecoRefs.current['tempoAEMitral']}
                      value={formData.tempoAEMitral}
                      onChange={handleChange}
                      onKeyDown={e => handleEcoKeyDown(e, 'tempoAEMitral')}
                    />
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group>
                    <Form.Label>Índice Cardíaco</Form.Label>
                    <Form.Control
                      name="indiceCardiaco"
                      style={editStyle}
                      type="number"
                      ref={ecoRefs.current['indiceCardiaco']}
                      value={formData.indiceCardiaco}
                      onChange={handleChange}
                      onKeyDown={e => handleEcoKeyDown(e, 'indiceCardiaco')}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col xs="auto">
                  <Form.Label>Comentários da Função Diastólica</Form.Label>
                  <div className="d-flex flex-wrap gap-1">
                    {formData.comentariosDiast.map((val, i) => (
                      <Form.Control
                        key={i}
                        ref={ecoArrayRefs.current.comentariosDiast?.[i]}
                        size="sm"
                        style={arrayStyle}
                        value={val ?? ''}
                        onChange={e =>
                          handleArrayChange(e, 'comentariosDiast', i)
                        }
                        onKeyDown={e =>
                          handleEcoArrayKeyDown(e, 'comentariosDiast', i)
                        }
                      />
                    ))}
                    <DeleteButton
                      field="comentariosDiast"
                      onDelete={handleClearArrayAndDelete}
                    />
                  </div>
                  {eco?.avDiast?.map((o, index) => (
                    <span key={index} className={styles.spanStyle}>
                      [ {o.CODAVDIAST} —{' '}
                      {o.DESCRICAO || 'Descrição não encontrada'} ]
                    </span>
                  ))}
                </Col>
              </Row>
            </Form.Group>

            {/* IV - Valva Mitral e Átrio Esquerdo */}
            <h5>IV - Valva Mitral e Átrio Esquerdo</h5>
            <Form.Group>
              <Row>
                <Col>
                  <Form.Label>Diâmetro do AE (cm)</Form.Label>
                  <Form.Control
                    name="diamAE"
                    style={editStyle}
                    type="number"
                    ref={ecoRefs.current['diamAE']}
                    value={formData.diamAE}
                    onChange={handleChange}
                    onKeyDown={e => handleEcoKeyDown(e, 'diamAE')}
                  />
                </Col>
                <Col>
                  <Form.Label>Índice Vol. AE</Form.Label>
                  <Form.Control
                    name="indiceVolAE"
                    style={editStyle}
                    type="number"
                    ref={ecoRefs.current['indiceVolAE']}
                    value={formData.indiceVolAE}
                    onChange={handleChange}
                    onKeyDown={e => handleEcoKeyDown(e, 'indiceVolAE')}
                  />
                </Col>
              </Row>
              <Row>
                <Col xs="auto">
                  <Form.Label>Átrio Esquerdo</Form.Label>
                  <div className="d-flex flex-wrap gap-1">
                    {formData.atrioEsq.map((val, i) => (
                      <Form.Control
                        key={i}
                        ref={ecoArrayRefs.current.atrioEsq?.[i]}
                        size="sm"
                        style={arrayStyle}
                        value={val ?? ''}
                        onChange={e => handleArrayChange(e, 'atrioEsq', i)}
                        onKeyDown={e => handleEcoArrayKeyDown(e, 'atrioEsq', i)}
                      />
                    ))}
                    <DeleteButton
                      field="atrioEsq"
                      onDelete={handleClearArrayAndDelete}
                    />
                  </div>
                  {eco?.atrioEsq?.map((o, index) => (
                    <span key={index} className={styles.spanStyle}>
                      [ {o.CODAE} — {o.DESCRICAO || 'Descrição não encontrada'}{' '}
                      ]
                    </span>
                  ))}{' '}
                </Col>
                <Col xs="auto">
                  <Form.Label>Valva Mitral</Form.Label>
                  <div className="d-flex flex-wrap gap-1">
                    {formData.valvaMitral.map((val, i) => (
                      <Form.Control
                        key={i}
                        ref={ecoArrayRefs.current.valvaMitral?.[i]}
                        size="sm"
                        style={arrayStyle}
                        value={val ?? ''}
                        onChange={e => handleArrayChange(e, 'valvaMitral', i)}
                        onKeyDown={e =>
                          handleEcoArrayKeyDown(e, 'valvaMitral', i)
                        }
                      />
                    ))}
                    <DeleteButton
                      field="valvaMitral"
                      onDelete={handleClearArrayAndDelete}
                    />
                  </div>
                  {eco?.valvaMitral?.map((o, index) => (
                    <span key={index} className={styles.spanStyle}>
                      [ {o.CODVM} — {o.DESCRICAO || 'Descrição não encontrada'}{' '}
                      ]
                    </span>
                  ))}{' '}
                </Col>
              </Row>
            </Form.Group>

            {/* V - Valva Aórtica e Aorta */}
            <h5>V - Valva Aórtica e Aorta</h5>
            <Form.Group>
              <Row>
                <Col>
                  <Form.Label>Diâm. Valv. AO (mm)</Form.Label>
                  <Form.Control
                    name="diamAO"
                    style={editStyle}
                    type="number"
                    ref={ecoRefs.current['diamAO']}
                    value={formData.diamAO}
                    onChange={handleChange}
                    onKeyDown={e => handleEcoKeyDown(e, 'diamAO')}
                  />
                </Col>
                <Col>
                  <Form.Label>Diâmetro da Via de Saída do VE (cm)</Form.Label>
                  <Form.Control
                    name="diamSaidaVE"
                    style={editStyle}
                    type="number"
                    ref={ecoRefs.current['diamSaidaVE']}
                    value={formData.diamSaidaVE}
                    onChange={handleChange}
                    onKeyDown={e => handleEcoKeyDown(e, 'diamSaidaVE')}
                  />
                </Col>
              </Row>
              <Row>
                <Col xs="auto">
                  <Form.Label>Valva Aórtica</Form.Label>
                  <div className="d-flex flex-wrap gap-1">
                    {formData.valvaAortica.map((val, i) => (
                      <Form.Control
                        key={i}
                        ref={ecoArrayRefs.current.valvaAortica?.[i]}
                        size="sm"
                        style={arrayStyle}
                        value={val ?? ''}
                        onChange={e => handleArrayChange(e, 'valvaAortica', i)}
                        onKeyDown={e =>
                          handleEcoArrayKeyDown(e, 'valvaAortica', i)
                        }
                      />
                    ))}
                    <DeleteButton
                      field="valvaAortica"
                      onDelete={handleClearArrayAndDelete}
                    />
                  </div>
                  {eco?.valvaAO?.map((o, index) => (
                    <span key={index} className={styles.spanStyle}>
                      [ {o.CODVAO} — {o.DESCRICAO || 'Descrição não encontrada'}{' '}
                      ]
                    </span>
                  ))}{' '}
                </Col>
                <Col xs="auto">
                  <Form.Label>Aorta</Form.Label>
                  <div className="d-flex flex-wrap gap-1">
                    {formData.aorta.map((val, i) => (
                      <Form.Control
                        key={i}
                        ref={ecoArrayRefs.current.aorta?.[i]}
                        size="sm"
                        style={arrayStyle}
                        value={val ?? ''}
                        onChange={e => handleArrayChange(e, 'aorta', i)}
                        onKeyDown={e => handleEcoArrayKeyDown(e, 'aorta', i)}
                      />
                    ))}
                    <DeleteButton
                      field="aorta"
                      onDelete={handleClearArrayAndDelete}
                    />
                  </div>
                  {eco?.aorta?.map((o, index) => (
                    <span key={index} className={styles.spanStyle}>
                      [ {o.CODAORTA} —{' '}
                      {o.DESCRICAO || 'Descrição não encontrada'} ]
                    </span>
                  ))}{' '}
                </Col>
              </Row>
            </Form.Group>

            {/* VI - Cavidades Direita e Valvas */}
            <h5>VI - Cavidades Direita e Valvas</h5>
            <Form.Group>
              <Row>
                <Col xs="auto">
                  <Form.Label>Átrio Direito</Form.Label>
                  <div className="d-flex flex-wrap gap-1">
                    {formData.atrioDireito.map((val, i) => (
                      <Form.Control
                        key={i}
                        ref={ecoArrayRefs.current.atrioDireito?.[i]}
                        size="sm"
                        style={arrayStyle}
                        value={val ?? ''}
                        onChange={e => handleArrayChange(e, 'atrioDireito', i)}
                        onKeyDown={e =>
                          handleEcoArrayKeyDown(e, 'atrioDireito', i)
                        }
                      />
                    ))}
                    <DeleteButton
                      field="atrioDireito"
                      onDelete={handleClearArrayAndDelete}
                    />
                  </div>
                  {eco?.atrioDir?.map((o, index) => (
                    <span key={index} className={styles.spanStyle}>
                      [ {o.CODAD} — {o.DESCRICAO || 'Descrição não encontrada'}{' '}
                      ]
                    </span>
                  ))}{' '}
                </Col>

                <Col xs="auto">
                  <Form.Label>Veia Cava Inferior</Form.Label>
                  <div className="d-flex flex-wrap gap-1">
                    {formData.veiaCavaInferior.map((val, i) => (
                      <Form.Control
                        key={i}
                        ref={ecoArrayRefs.current.veiaCavaInferior?.[i]}
                        size="sm"
                        style={arrayStyle}
                        value={val ?? ''}
                        onChange={e =>
                          handleArrayChange(e, 'veiaCavaInferior', i)
                        }
                        onKeyDown={e =>
                          handleEcoArrayKeyDown(e, 'veiaCavaInferior', i)
                        }
                      />
                    ))}
                    <DeleteButton
                      field="veiaCavaInferior"
                      onDelete={handleClearArrayAndDelete}
                    />
                  </div>
                  {eco?.veiaCavaInf?.map((o, index) => (
                    <span key={index} className={styles.spanStyle}>
                      [ {o.CODVCINF} —{' '}
                      {o.DESCRICAO || 'Descrição não encontrada'} ]
                    </span>
                  ))}{' '}
                </Col>
              </Row>

              <Row>
                <Col xs="auto">
                  <Form.Label>Ventrículo Direito</Form.Label>
                  <div className="d-flex flex-wrap gap-1">
                    {formData.ventriculoDireito.map((val, i) => (
                      <Form.Control
                        key={i}
                        ref={ecoArrayRefs.current.ventriculoDireito?.[i]}
                        size="sm"
                        style={arrayStyle}
                        value={val ?? ''}
                        onChange={e =>
                          handleArrayChange(e, 'ventriculoDireito', i)
                        }
                        onKeyDown={e =>
                          handleEcoArrayKeyDown(e, 'ventriculoDireito', i)
                        }
                      />
                    ))}
                    <DeleteButton
                      field="ventriculoDireito"
                      onDelete={handleClearArrayAndDelete}
                    />
                  </div>
                  {eco?.ventriDir?.map((o, index) => (
                    <span key={index} className={styles.spanStyle}>
                      [ {o.CODVD} — {o.DESCRICAO || 'Descrição não encontrada'}{' '}
                      ]
                    </span>
                  ))}{' '}
                </Col>{' '}
                <Col xs="auto">
                  <Form.Label>Valva Tricúspide</Form.Label>
                  <div className="d-flex flex-wrap gap-1">
                    {formData.valvaTricuspide.map((val, i) => (
                      <Form.Control
                        key={i}
                        ref={ecoArrayRefs.current.valvaTricuspide?.[i]}
                        size="sm"
                        style={arrayStyle}
                        value={val ?? ''}
                        onChange={e =>
                          handleArrayChange(e, 'valvaTricuspide', i)
                        }
                        onKeyDown={e =>
                          handleEcoArrayKeyDown(e, 'valvaTricuspide', i)
                        }
                      />
                    ))}
                    <DeleteButton
                      field="valvaTricuspide"
                      onDelete={handleClearArrayAndDelete}
                    />
                  </div>
                  {eco?.valvaTricu?.map((o, index) => (
                    <span key={index} className={styles.spanStyle}>
                      [ {o.CODVT} — {o.DESCRICAO || 'Descrição não encontrada'}{' '}
                      ]
                    </span>
                  ))}{' '}
                </Col>
              </Row>
              <Row>
                <Col>
                  <Form.Label>Diâm. do Anel Valv. Pulmonar (cm)</Form.Label>
                  <Form.Control
                    style={editStyle}
                    type="number"
                    name="diamAnelPulmonar"
                    // ref={ecoRefs.current[0]}
                    value={formData.diamAnelPulmonar}
                    onChange={handleChange}
                  // onKeyDown={e => handleEcoKeyDown(e, 0)}
                  />
                </Col>{' '}
                <Col xs="auto">
                  <Form.Label>Valva Pulmonar</Form.Label>
                  <div className="d-flex flex-wrap gap-1">
                    {formData.valvaPulmonar.map((val, i) => (
                      <Form.Control
                        key={i}
                        ref={ecoArrayRefs.current.valvaPulmonar?.[i]}
                        size="sm"
                        style={arrayStyle}
                        value={val ?? ''}
                        onChange={e => handleArrayChange(e, 'valvaPulmonar', i)}
                        onKeyDown={e =>
                          handleEcoArrayKeyDown(e, 'valvaPulmonar', i)
                        }
                      />
                    ))}
                    <DeleteButton
                      field="valvaPulmonar"
                      onDelete={handleClearArrayAndDelete}
                    />
                  </div>
                  {eco?.valvaPulmo?.map((o, index) => (
                    <span key={index} className={styles.spanStyle}>
                      [ {o.CODVP} — {o.DESCRICAO || 'Descrição não encontrada'}{' '}
                      ]
                    </span>
                  ))}{' '}
                </Col>
              </Row>
            </Form.Group>

            <h5>VII - Pericárdio</h5>
            <Form.Group>
              <Row>
                <Col xs="auto">
                  <div className="d-flex flex-wrap gap-1">
                    {formData.pericardio.map((val, i) => (
                      <Form.Control
                        key={i}
                        ref={ecoArrayRefs.current.pericardio?.[i]}
                        size="sm"
                        style={arrayStyle}
                        value={val ?? ''}
                        onChange={e => handleArrayChange(e, 'pericardio', i)}
                        onKeyDown={e =>
                          handleEcoArrayKeyDown(e, 'pericardio', i)
                        }
                      />
                    ))}
                    <DeleteButton
                      field="pericardio"
                      onDelete={handleClearArrayAndDelete}
                    />
                  </div>
                  {eco?.pericardio?.map((o, index) => (
                    <span key={index} className={styles.spanStyle}>
                      [ {o.CODPERICARDIO} —{' '}
                      {o.DESCRICAO || 'Descrição não encontrada'} ]
                    </span>
                  ))}{' '}
                </Col>
              </Row>
            </Form.Group>

            {/* ========================================================= */}
            {/* VIII - ESTUDO CONGÊNITO                                 */}
            {/* ========================================================= */}
            <div
              style={{
                backgroundColor: '#f0fdf4',
                border: '2px solid #22c55e',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '24px',
                marginTop: '16px',
              }}
            >
              <div className="d-flex align-items-center mb-3">
                <h4 style={{ color: '#15803d', fontWeight: 'bold', margin: 0 }}>
                  VIII - Estudo Congênito
                </h4>
                <span
                  style={{
                    marginLeft: '12px',
                    backgroundColor: '#dcfce7',
                    color: '#166534',
                    padding: '2px 8px',
                    borderRadius: '4px',
                    fontSize: '0.85rem',
                    fontWeight: '600',
                  }}
                >
                  Ecocardiograma Pediátrico / Congênito
                </span>
              </div>

              {/* BLOCO 1: SITUS E SEPTOS */}
              <h6
                style={{
                  color: '#166534',
                  fontWeight: 700,
                  borderBottom: '1px solid #bbf7d0',
                  paddingBottom: '4px',
                }}
              >
                1. Situs e Septos
              </h6>
              <Row className="mb-3">
                <Col xs="auto">
                  <Form.Label className="font-weight-bold">
                    Situs Cardíaco
                  </Form.Label>
                  <div className="d-flex flex-wrap gap-1">
                    {formData.situs.map((val, i) => (
                      <Form.Control
                        key={i}
                        ref={ecoArrayRefs.current.situs?.[i]}
                        size="sm"
                        style={arrayStyle}
                        value={val ?? ''}
                        onChange={e => handleArrayChange(e, 'situs', i)}
                        onKeyDown={e => handleEcoArrayKeyDown(e, 'situs', i)}
                      />
                    ))}
                    <DeleteButton
                      field="situs"
                      onDelete={handleClearArrayAndDelete}
                    />
                  </div>
                  {eco?.situs?.map((o, index) => (
                    <span key={index} className={styles.spanStyle}>
                      [ {o.CODSITUS} —{' '}
                      {o.DESCRICAO || 'Descrição não encontrada'} ]
                    </span>
                  ))}
                </Col>
              </Row>

              <Row className="mb-3">
                <Col xs="auto">
                  <Form.Label className="font-weight-bold">
                    Septo Interatrial (Código + Medida CIA em cm)
                  </Form.Label>
                  <div className="d-flex flex-wrap gap-2 align-items-center">
                    {formData.septoIA.map((val, i) => (
                      <div
                        key={i}
                        className="d-flex align-items-center"
                        style={{
                          backgroundColor: '#fff',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          border: '1px solid #d1d5db',
                        }}
                      >
                        <Form.Control
                          ref={ecoArrayRefs.current.septoIA?.[i]}
                          size="sm"
                          style={{ ...arrayStyle, width: '45px', margin: 0 }}
                          // placeholder="Cód"
                          title="Código Septo IA"
                          value={val ?? ''}
                          onChange={e => handleArrayChange(e, 'septoIA', i)}
                          onKeyDown={e =>
                            handleEcoArrayKeyDown(e, 'septoIA', i)
                          }
                        />
                        <span
                          style={{
                            fontSize: '0.8rem',
                            margin: '0 4px',
                            color: '#6b7280',
                          }}
                        >
                          CIA:
                        </span>
                        <Form.Control
                          ref={ecoArrayRefs.current.medidaCIA?.[i]}
                          size="sm"
                          style={{
                            ...editStyle,
                            width: '65px',
                            margin: 0,
                            padding: '2px 4px',
                          }}
                          // placeholder="cm"
                          title="Medida CIA (cm)"
                          value={formData.medidaCIA[i] ?? ''}
                          onChange={e =>
                            handleFloatArrayChange(e, 'medidaCIA', i)
                          }
                          onKeyDown={e =>
                            handleEcoArrayKeyDown(e, 'medidaCIA', i)
                          }
                        />
                      </div>
                    ))}
                    <DeleteButton
                      field="septoIA"
                      onDelete={handleClearArrayAndDelete}
                    />
                  </div>
                  {eco?.septoIA?.map((o, index) => (
                    <span key={index} className={styles.spanStyle}>
                      [ {o.CODSEPTO} —{' '}
                      {o.DESCRICAO || 'Descrição não encontrada'}
                      {o.MEDIDA ? ` (CIA: ${o.MEDIDA} cm)` : ''} ]
                    </span>
                  ))}
                </Col>
              </Row>

              <Row className="mb-3">
                <Col xs="auto">
                  <Form.Label className="font-weight-bold">
                    Septo Interventricular (Código + Região + Medida CIV em cm)
                  </Form.Label>
                  <div className="d-flex flex-wrap gap-2 align-items-center">
                    {formData.septoIV.map((val, i) => (
                      <div
                        key={i}
                        className="d-flex align-items-center"
                        style={{
                          backgroundColor: '#fff',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          border: '1px solid #d1d5db',
                        }}
                      >
                        <Form.Control
                          ref={ecoArrayRefs.current.septoIV?.[i]}
                          size="sm"
                          style={{ ...arrayStyle, width: '45px', margin: 0 }}
                          // placeholder="Cód"
                          title="Código Septo IV"
                          value={val ?? ''}
                          onChange={e => handleArrayChange(e, 'septoIV', i)}
                          onKeyDown={e =>
                            handleEcoArrayKeyDown(e, 'septoIV', i)
                          }
                        />
                        <span
                          style={{
                            fontSize: '0.8rem',
                            margin: '0 4px',
                            color: '#6b7280',
                          }}
                        >
                          Reg:
                        </span>
                        <Form.Control
                          ref={ecoArrayRefs.current.regiaoSeptoIV?.[i]}
                          size="sm"
                          style={{ ...arrayStyle, width: '45px', margin: 0 }}
                          // placeholder="Reg"
                          title="Código da Região"
                          value={formData.regiaoSeptoIV[i] ?? ''}
                          onChange={e =>
                            handleArrayChange(e, 'regiaoSeptoIV', i)
                          }
                          onKeyDown={e =>
                            handleEcoArrayKeyDown(e, 'regiaoSeptoIV', i)
                          }
                        />
                        <span
                          style={{
                            fontSize: '0.8rem',
                            margin: '0 4px',
                            color: '#6b7280',
                          }}
                        >
                          CIV:
                        </span>
                        <Form.Control
                          ref={ecoArrayRefs.current.medidaCIV?.[i]}
                          size="sm"
                          style={{
                            ...editStyle,
                            width: '65px',
                            margin: 0,
                            padding: '2px 4px',
                          }}
                          // placeholder="cm"
                          title="Medida CIV (cm)"
                          value={formData.medidaCIV[i] ?? ''}
                          onChange={e =>
                            handleFloatArrayChange(e, 'medidaCIV', i)
                          }
                          onKeyDown={e =>
                            handleEcoArrayKeyDown(e, 'medidaCIV', i)
                          }
                        />
                      </div>
                    ))}
                    <DeleteButton
                      field="septoIV"
                      onDelete={handleClearArrayAndDelete}
                    />
                  </div>
                  {eco?.septoIV?.map((o, index) => (
                    <span key={index} className={styles.spanStyle}>
                      [ {o.CODSEPTO} — {o.septoIV || 'Descrição não encontrada'}
                      {o.MEDIDA ? ` (CIV: ${o.MEDIDA} cm)` : ''} ]
                    </span>
                  ))}
                </Col>
              </Row>

              {/* BLOCO 2: HEMODINÂMICA E CÁLCULOS */}
              <h6
                style={{
                  color: '#166534',
                  fontWeight: 700,
                  borderBottom: '1px solid #bbf7d0',
                  paddingBottom: '4px',
                  marginTop: '16px',
                }}
              >
                2. Hemodinâmica Congênita & Relação QP/QS
              </h6>
              <Row className="mb-2">
                <Col md={3}>
                  <Form.Label>Diâmetro VSVD (cm)</Form.Label>
                  <Form.Control
                    name="diametroVSVD"
                    style={editStyle}
                    type="number"
                    step="0.01"
                    ref={ecoRefs.current['diametroVSVD']}
                    value={formData.diametroVSVD}
                    onChange={handleChange}
                    onKeyDown={e => handleEcoKeyDown(e, 'diametroVSVD')}
                  />
                </Col>
                <Col md={3}>
                  <Form.Label>Gradiente VEVD (mmHg)</Form.Label>
                  <Form.Control
                    name="gradienteVEVD"
                    style={editStyle}
                    type="number"
                    step="0.01"
                    ref={ecoRefs.current['gradienteVEVD']}
                    value={formData.gradienteVEVD}
                    onChange={handleChange}
                    onKeyDown={e => handleEcoKeyDown(e, 'gradienteVEVD')}
                  />
                </Col>
                <Col md={3}>
                  <Form.Label>VTI Pulmonar</Form.Label>
                  <Form.Control
                    name="vtiPulmonar"
                    style={editStyle}
                    type="number"
                    step="0.01"
                    ref={ecoRefs.current['vtiPulmonar']}
                    value={formData.vtiPulmonar}
                    onChange={handleChange}
                    onKeyDown={e => handleEcoKeyDown(e, 'vtiPulmonar')}
                  />
                </Col>
                <Col md={3}>
                  <Form.Label>VTI VSVE (cm)</Form.Label>
                  <Form.Control
                    name="vtiVSVE"
                    style={editStyle}
                    type="number"
                    step="0.01"
                    ref={ecoRefs.current['vtiVSVE']}
                    value={formData.vtiVSVE}
                    onChange={handleChange}
                    onKeyDown={e => handleEcoKeyDown(e, 'vtiVSVE')}
                  />
                </Col>
              </Row>

              {/* Resultados Calculados em tempo real */}
              <div
                style={{
                  backgroundColor: '#ffffff',
                  border: '1px solid #86efac',
                  borderRadius: '6px',
                  padding: '10px 14px',
                  marginBottom: '16px',
                }}
              >
                <Row className="align-items-center text-center">
                  <Col md={3}>
                    <small className="text-muted d-block font-weight-bold">
                      Fluxo Pulmonar
                    </small>
                    <span
                      style={{
                        fontSize: '1.05rem',
                        fontWeight: 'bold',
                        color: '#1e3a8a',
                      }}
                    >
                      {fluxoPulmonar}
                    </span>
                  </Col>
                  <Col md={3}>
                    <small className="text-muted d-block font-weight-bold">
                      Fluxo Sistêmico
                    </small>
                    <span
                      style={{
                        fontSize: '1.05rem',
                        fontWeight: 'bold',
                        color: '#1e3a8a',
                      }}
                    >
                      {fluxoSistemico}
                    </span>
                  </Col>
                  <Col md={3}>
                    <small className="text-muted d-block font-weight-bold">
                      Relação QP/QS (Rel P/S)
                    </small>
                    <span
                      style={{
                        fontSize: '1.15rem',
                        fontWeight: '900',
                        color: '#047857',
                      }}
                    >
                      {relacaoPS}
                    </span>
                  </Col>
                  <Col md={3}>
                    <small className="text-muted d-block font-weight-bold">
                      Estimativa PS VD (PSAP)
                    </small>
                    <span
                      style={{
                        fontSize: '1.15rem',
                        fontWeight: '900',
                        color: '#b91c1c',
                      }}
                    >
                      {estimativaPSVD ? `${estimativaPSVD} mmHg` : '—'}
                    </span>
                  </Col>
                </Row>
              </div>

              {/* BLOCO 3: CONCORDÂNCIAS E VIAS */}
              <h6
                style={{
                  color: '#166534',
                  fontWeight: 700,
                  borderBottom: '1px solid #bbf7d0',
                  paddingBottom: '4px',
                  marginTop: '16px',
                }}
              >
                3. Concordâncias, Vias de Saída e Artérias
              </h6>
              <Row className="mb-3">
                <Col xs="auto">
                  <Form.Label className="font-weight-bold">
                    Concordância Atrioventricular
                  </Form.Label>
                  <div className="d-flex flex-wrap gap-1">
                    {formData.concordAV.map((val, i) => (
                      <Form.Control
                        key={i}
                        ref={ecoArrayRefs.current.concordAV?.[i]}
                        size="sm"
                        style={arrayStyle}
                        value={val ?? ''}
                        onChange={e => handleArrayChange(e, 'concordAV', i)}
                        onKeyDown={e =>
                          handleEcoArrayKeyDown(e, 'concordAV', i)
                        }
                      />
                    ))}
                    <DeleteButton
                      field="concordAV"
                      onDelete={handleClearArrayAndDelete}
                    />
                  </div>
                  {eco?.concordAV?.map((o, index) => (
                    <span key={index} className={styles.spanStyle}>
                      [ {o.CODCONCORDAV} —{' '}
                      {o.DESCRICAO || 'Descrição não encontrada'} ]
                    </span>
                  ))}
                </Col>
              </Row>

              <Row className="mb-3">
                <Col xs="auto">
                  <Form.Label className="font-weight-bold">
                    Vias de Saída
                  </Form.Label>
                  <div className="d-flex flex-wrap gap-1">
                    {formData.viaSaida.map((val, i) => (
                      <Form.Control
                        key={i}
                        ref={ecoArrayRefs.current.viaSaida?.[i]}
                        size="sm"
                        style={arrayStyle}
                        value={val ?? ''}
                        onChange={e => handleArrayChange(e, 'viaSaida', i)}
                        onKeyDown={e => handleEcoArrayKeyDown(e, 'viaSaida', i)}
                      />
                    ))}
                    <DeleteButton
                      field="viaSaida"
                      onDelete={handleClearArrayAndDelete}
                    />
                  </div>
                  {eco?.ecoVS?.map((o, index) => (
                    <span key={index} className={styles.spanStyle}>
                      [ {o.CODVS} — {o.DESCRICAO || 'Descrição não encontrada'}{' '}
                      ]
                    </span>
                  ))}
                </Col>
              </Row>

              <Row className="mb-3">
                <Col xs="auto">
                  <Form.Label className="font-weight-bold">
                    Concordância Ventrículo-Arterial
                  </Form.Label>
                  <div className="d-flex flex-wrap gap-1">
                    {formData.concordVA.map((val, i) => (
                      <Form.Control
                        key={i}
                        ref={ecoArrayRefs.current.concordVA?.[i]}
                        size="sm"
                        style={arrayStyle}
                        value={val ?? ''}
                        onChange={e => handleArrayChange(e, 'concordVA', i)}
                        onKeyDown={e =>
                          handleEcoArrayKeyDown(e, 'concordVA', i)
                        }
                      />
                    ))}
                    <DeleteButton
                      field="concordVA"
                      onDelete={handleClearArrayAndDelete}
                    />
                  </div>
                  {eco?.concordVA?.map((o, index) => (
                    <span key={index} className={styles.spanStyle}>
                      [ {o.CODCONCORDVA} —{' '}
                      {o.DESCRICAO || 'Descrição não encontrada'} ]
                    </span>
                  ))}
                </Col>
              </Row>

              <Row className="mb-3">
                <Col xs="auto">
                  <Form.Label className="font-weight-bold">
                    Artéria Pulmonar
                  </Form.Label>
                  <div className="d-flex flex-wrap gap-1">
                    {formData.arteriaPulmonar.map((val, i) => (
                      <Form.Control
                        key={i}
                        ref={ecoArrayRefs.current.arteriaPulmonar?.[i]}
                        size="sm"
                        style={arrayStyle}
                        value={val ?? ''}
                        onChange={e =>
                          handleArrayChange(e, 'arteriaPulmonar', i)
                        }
                        onKeyDown={e =>
                          handleEcoArrayKeyDown(e, 'arteriaPulmonar', i)
                        }
                      />
                    ))}
                    <DeleteButton
                      field="arteriaPulmonar"
                      onDelete={handleClearArrayAndDelete}
                    />
                  </div>
                  {eco?.artP?.map((o, index) => (
                    <span key={index} className={styles.spanStyle}>
                      [ {o.CODARTP} —{' '}
                      {o.DESCRICAO || 'Descrição não encontrada'} ]
                    </span>
                  ))}
                </Col>
              </Row>

              <Row className="mb-3">
                <Col xs="auto">
                  <Form.Label className="font-weight-bold">
                    Artéria Coronária
                  </Form.Label>
                  <div className="d-flex flex-wrap gap-1">
                    {formData.arteriaCoronaria.map((val, i) => (
                      <Form.Control
                        key={i}
                        ref={ecoArrayRefs.current.arteriaCoronaria?.[i]}
                        size="sm"
                        style={arrayStyle}
                        value={val ?? ''}
                        onChange={e =>
                          handleArrayChange(e, 'arteriaCoronaria', i)
                        }
                        onKeyDown={e =>
                          handleEcoArrayKeyDown(e, 'arteriaCoronaria', i)
                        }
                      />
                    ))}
                    <DeleteButton
                      field="arteriaCoronaria"
                      onDelete={handleClearArrayAndDelete}
                    />
                  </div>
                  {eco?.artC?.map((o, index) => (
                    <span key={index} className={styles.spanStyle}>
                      [ {o.CODARTC} —{' '}
                      {o.DESCRICAO || 'Descrição não encontrada'} ]
                    </span>
                  ))}
                </Col>
              </Row>

              {/* BLOCO 4: ARCOS E OUTRAS ESTRUTURAS */}
              <h6
                style={{
                  color: '#166534',
                  fontWeight: 700,
                  borderBottom: '1px solid #bbf7d0',
                  paddingBottom: '4px',
                  marginTop: '16px',
                }}
              >
                4. Arcos e Outras Estruturas
              </h6>
              <Row className="mb-3">
                <Col xs="auto">
                  <Form.Label className="font-weight-bold">
                    Arco Aórtico
                  </Form.Label>
                  <div className="d-flex flex-wrap gap-1">
                    {formData.arcoAortico.map((val, i) => (
                      <Form.Control
                        key={i}
                        ref={ecoArrayRefs.current.arcoAortico?.[i]}
                        size="sm"
                        style={arrayStyle}
                        value={val ?? ''}
                        onChange={e => handleArrayChange(e, 'arcoAortico', i)}
                        onKeyDown={e =>
                          handleEcoArrayKeyDown(e, 'arcoAortico', i)
                        }
                      />
                    ))}
                    <DeleteButton
                      field="arcoAortico"
                      onDelete={handleClearArrayAndDelete}
                    />
                  </div>
                  {eco?.arcoAO?.map((o, index) => (
                    <span key={index} className={styles.spanStyle}>
                      [ {o.CODARCOAO} —{' '}
                      {o.DESCRICAO || 'Descrição não encontrada'} ]
                    </span>
                  ))}
                </Col>
              </Row>

              <Row className="mb-3">
                <Col xs="auto">
                  <Form.Label className="font-weight-bold">
                    Conexão Veno-Atrial
                  </Form.Label>
                  <div className="d-flex flex-wrap gap-1">
                    {formData.conexaoVenoAtrial.map((val, i) => (
                      <Form.Control
                        key={i}
                        ref={ecoArrayRefs.current.conexaoVenoAtrial?.[i]}
                        size="sm"
                        style={arrayStyle}
                        value={val ?? ''}
                        onChange={e =>
                          handleArrayChange(e, 'conexaoVenoAtrial', i)
                        }
                        onKeyDown={e =>
                          handleEcoArrayKeyDown(e, 'conexaoVenoAtrial', i)
                        }
                      />
                    ))}
                    <DeleteButton
                      field="conexaoVenoAtrial"
                      onDelete={handleClearArrayAndDelete}
                    />
                  </div>
                  {eco?.conexao?.map((o, index) => (
                    <span key={index} className={styles.spanStyle}>
                      [ {o.CODCONEXAO} —{' '}
                      {o.DESCRICAO || 'Descrição não encontrada'} ]
                    </span>
                  ))}
                </Col>
              </Row>

              <Row className="mb-3">
                <Col xs="auto">
                  <Form.Label className="font-weight-bold">
                    Estruturas Anômalas
                  </Form.Label>
                  <div className="d-flex flex-wrap gap-1">
                    {formData.estruturasAnomalas.map((val, i) => (
                      <Form.Control
                        key={i}
                        ref={ecoArrayRefs.current.estruturasAnomalas?.[i]}
                        size="sm"
                        style={arrayStyle}
                        value={val ?? ''}
                        onChange={e =>
                          handleArrayChange(e, 'estruturasAnomalas', i)
                        }
                        onKeyDown={e =>
                          handleEcoArrayKeyDown(e, 'estruturasAnomalas', i)
                        }
                      />
                    ))}
                    <DeleteButton
                      field="estruturasAnomalas"
                      onDelete={handleClearArrayAndDelete}
                    />
                  </div>
                  {eco?.estrutura?.map((o, index) => (
                    <span key={index} className={styles.spanStyle}>
                      [ {o.CODESTRUTURA} —{' '}
                      {o.DESCRICAO || 'Descrição não encontrada'} ]
                    </span>
                  ))}
                </Col>
              </Row>
            </div>

            <h5>IX - Valvulopatia da Valva Mitral</h5>
            <Form.Group>
              <Row>
                <Col>
                  <Form.Label>Área (cm²)</Form.Label>
                  <Form.Control
                    name="areaMitral"
                    style={editStyle}
                    type="number"
                    ref={ecoRefs.current['areaMitral']}
                    value={formData.areaMitral}
                    onChange={handleChange}
                    onKeyDown={e => handleEcoKeyDown(e, 'areaMitral')}
                  />
                </Col>
                <Col>
                  <Form.Label>Gradiente Médio (mm Hg)</Form.Label>
                  <Form.Control
                    name="gradienteMitral"
                    style={editStyle}
                    type="number"
                    ref={ecoRefs.current['gradienteMitral']}
                    value={formData.gradienteMitral}
                    onChange={handleChange}
                    onKeyDown={e => handleEcoKeyDown(e, 'gradienteMitral')}
                  />
                </Col>
                <Col xs="auto">
                  <Form.Label>Refluxo</Form.Label>
                  <div className="d-flex flex-wrap gap-1">
                    {formData.refluxoMitral.map((val, i) => (
                      <Form.Control
                        key={i}
                        ref={ecoArrayRefs.current.refluxoMitral?.[i]}
                        size="sm"
                        style={arrayStyle}
                        value={val ?? ''}
                        onChange={e => handleArrayChange(e, 'refluxoMitral', i)}
                        onKeyDown={e =>
                          handleEcoArrayKeyDown(e, 'refluxoMitral', i)
                        }
                      />
                    ))}
                    <DeleteButton
                      field="refluxoMitral"
                      onDelete={handleClearArrayAndDelete}
                    />
                  </div>
                  {eco?.refVM?.map((o, index) => (
                    <span key={index} className={styles.spanStyle}>
                      [ {o.CODREFVM} —{' '}
                      {o.DESCRICAO || 'Descrição não encontrada'} ]
                    </span>
                  ))}{' '}
                </Col>
              </Row>
              <Row>
                <Col>
                  <Form.Label>Pisa</Form.Label>
                  <Form.Control
                    name="pisaMitral"
                    style={editStyle}
                    type="number"
                    ref={ecoRefs.current['pisaMitral']}
                    value={formData.pisaMitral}
                    onChange={handleChange}
                    onKeyDown={e => handleEcoKeyDown(e, 'pisaMitral')}
                  />
                </Col>
                <Col>
                  <Form.Label>Volume Regurgitação</Form.Label>
                  <Form.Control
                    name="volumeRegMitral"
                    style={editStyle}
                    type="number"
                    ref={ecoRefs.current['volumeRegMitral']}
                    value={formData.volumeRegMitral}
                    onChange={handleChange}
                    onKeyDown={e => handleEcoKeyDown(e, 'volumeRegMitral')}
                  />
                </Col>
              </Row>
            </Form.Group>

            {/* IX - Valvulopatia da Valva Aórtica */}
            <h5>X - Valvulopatia da Valva Aórtica</h5>
            <Form.Group>
              <Row>
                <Col>
                  <Form.Label>Área (cm²)</Form.Label>
                  <Form.Control
                    name="areaAortica"
                    style={editStyle}
                    type="number"
                    ref={ecoRefs.current['areaAortica']}
                    value={formData.areaAortica}
                    onChange={handleChange}
                    onKeyDown={e => handleEcoKeyDown(e, 'areaAortica')}
                  />
                </Col>
                <Col>
                  <Form.Label>
                    Pisa (*Não está sendo usado mais no Delphi*)
                  </Form.Label>
                  <Form.Control
                    name="pisaAortica"
                    style={editStyle}
                    type="number"
                    ref={ecoRefs.current['pisaAortica']}
                    value={formData.pisaAortica}
                    onChange={handleChange}
                    onKeyDown={e => handleEcoKeyDown(e, 'pisaAortica')}
                  />
                </Col>
                <Col xs="auto">
                  <Form.Label>Refluxo</Form.Label>
                  <div className="d-flex flex-wrap gap-1">
                    {formData.refluxoAortica.map((val, i) => (
                      <Form.Control
                        key={i}
                        ref={ecoArrayRefs.current.refluxoAortica?.[i]}
                        size="sm"
                        style={arrayStyle}
                        value={val ?? ''}
                        onChange={e =>
                          handleArrayChange(e, 'refluxoAortica', i)
                        }
                        onKeyDown={e =>
                          handleEcoArrayKeyDown(e, 'refluxoAortica', i)
                        }
                      />
                    ))}
                    <DeleteButton
                      field="refluxoAortica"
                      onDelete={handleClearArrayAndDelete}
                    />
                  </div>
                  {eco?.refVAO?.map((o, index) => (
                    <span key={index} className={styles.spanStyle}>
                      [ {o.CODREFVAO} —{' '}
                      {o.DESCRICAO || 'Descrição não encontrada'} ]
                    </span>
                  ))}{' '}
                </Col>
              </Row>
              <Row>
                <Col>
                  <Form.Label>GMVAO (mm Hg)</Form.Label>
                  <Form.Control
                    name="gmvao"
                    style={editStyle}
                    type="number"
                    ref={ecoRefs.current['gmvao']}
                    value={formData.gmvao}
                    onChange={handleChange}
                    onKeyDown={e => handleEcoKeyDown(e, 'gmvao')}
                  />
                </Col>
                <Col>
                  <Form.Label>GMVSVE (mm Hg)</Form.Label>
                  <Form.Control
                    name="gmvsve"
                    style={editStyle}
                    type="number"
                    ref={ecoRefs.current['gmvsve']}
                    value={formData.gmvsve}
                    onChange={handleChange}
                    onKeyDown={e => handleEcoKeyDown(e, 'gmvsve')}
                  />
                </Col>
              </Row>
            </Form.Group>
            <hr />
            {/* XI - Valvulopatia de Valva Tricúspide/Pulmonar */}
            <h5>XI - Valvulopatia de Valva Tricúspide/Pulmonar</h5>
            <Form.Group>
              <Row>
                <Col>
                  <Form.Label>GMV Tric (mm Hg)</Form.Label>
                  <Form.Control
                    name="gmvTric"
                    style={editStyle}
                    type="number"
                    ref={ecoRefs.current['gmvTric']}
                    value={formData.gmvTric}
                    onChange={handleChange}
                    onKeyDown={e => handleEcoKeyDown(e, 'gmvTric')}
                  />
                </Col>
                <Col>
                  <Form.Label>GPIT (mm Hg)</Form.Label>
                  <Form.Control
                    name="gpit"
                    style={editStyle}
                    type="number"
                    ref={ecoRefs.current['gpit']}
                    value={formData.gpit}
                    onChange={handleChange}
                    onKeyDown={e => handleEcoKeyDown(e, 'gpit')}
                  />
                </Col>
                <Col xs="auto">
                  <Form.Label>Refluxo (Tric)</Form.Label>
                  <div className="d-flex flex-wrap gap-1">
                    {formData.refluxoTric.map((val, i) => (
                      <Form.Control
                        key={i}
                        ref={ecoArrayRefs.current.refluxoTric?.[i]}
                        size="sm"
                        style={arrayStyle}
                        value={val ?? ''}
                        onChange={e => handleArrayChange(e, 'refluxoTric', i)}
                        onKeyDown={e =>
                          handleEcoArrayKeyDown(e, 'refluxoTric', i)
                        }
                      />
                    ))}
                    <DeleteButton
                      field="refluxoTric"
                      onDelete={handleClearArrayAndDelete}
                    />
                  </div>
                  {eco?.refVT?.map((o, index) => (
                    <span key={index} className={styles.spanStyle}>
                      [ {o.CODREFVT} —{' '}
                      {o.DESCRICAO || 'Descrição não encontrada'} ]
                    </span>
                  ))}{' '}
                </Col>
              </Row>
              <Row>
                <Col>
                  <Form.Label>GMV Pulm (mm Hg)</Form.Label>
                  <Form.Control
                    name="gradientePulm"
                    style={editStyle}
                    type="number"
                    ref={ecoRefs.current['gradientePulm']}
                    value={formData.gradientePulm}
                    onChange={handleChange}
                    onKeyDown={e => handleEcoKeyDown(e, 'gradientePulm')}
                  />
                </Col>
                <Col xs="auto">
                  <Form.Label>Refluxo (Pulm)</Form.Label>
                  <div className="d-flex flex-wrap gap-1">
                    {formData.refluxoPulm.map((val, i) => (
                      <Form.Control
                        key={i}
                        ref={ecoArrayRefs.current.refluxoPulm?.[i]}
                        size="sm"
                        style={arrayStyle}
                        value={val ?? ''}
                        onChange={e => handleArrayChange(e, 'refluxoPulm', i)}
                        onKeyDown={e =>
                          handleEcoArrayKeyDown(e, 'refluxoPulm', i)
                        }
                      />
                    ))}
                    <DeleteButton
                      field="refluxoPulm"
                      onDelete={handleClearArrayAndDelete}
                    />
                  </div>
                  {eco?.refVP?.map((o, index) => (
                    <span key={index} className={styles.spanStyle}>
                      [ {o.CODREFVP} —{' '}
                      {o.DESCRICAO || 'Descrição não encontrada'} ]
                    </span>
                  ))}{' '}
                </Col>
              </Row>
            </Form.Group>

            <h5>XII - Próteses</h5>
            <Form.Group>
              <Row>
                <Col>
                  <Form.Label>Prótese Mitral</Form.Label>
                  <Form.Control
                    name="gradMitral"
                    style={editStyle}
                    type="number"
                    ref={ecoRefs.current['gradMitral']}
                    value={formData.gradMitral}
                    onChange={handleChange}
                    onKeyDown={e => handleEcoKeyDown(e, 'gradMitral')}
                  />{' '}
                  <Form.Label>Refluxo</Form.Label>
                  <div className="d-flex flex-wrap gap-1">
                    {formData.refluxoProtMitral.map((val, i) => (
                      <Form.Control
                        key={i}
                        ref={ecoArrayRefs.current.refluxoProtMitral?.[i]}
                        size="sm"
                        style={arrayStyle}
                        value={val ?? ''}
                        onChange={e =>
                          handleArrayChange(e, 'refluxoProtMitral', i)
                        }
                        onKeyDown={e =>
                          handleEcoArrayKeyDown(e, 'refluxoProtMitral', i)
                        }
                      />
                    ))}
                    <DeleteButton
                      field="refluxoProtMitral"
                      onDelete={handleClearArrayAndDelete}
                    />
                  </div>
                  {eco?.refluxoPM?.map((o, index) => (
                    <span key={index} className={styles.spanStyle}>
                      [ {o.CODREFPM} —{' '}
                      {o.DESCRICAO || 'Descrição não encontrada'} ]
                    </span>
                  ))}{' '}
                  <Form.Label>Intensidade</Form.Label>
                  <div className="d-flex flex-wrap gap-1">
                    {formData.intensidadeProtMitral.map((val, i) => (
                      <Form.Control
                        key={i}
                        ref={ecoArrayRefs.current.intensidadeProtMitral?.[i]}
                        size="sm"
                        style={arrayStyle}
                        value={val ?? ''}
                        onChange={e =>
                          handleArrayChange(e, 'intensidadeProtMitral', i)
                        }
                        onKeyDown={e =>
                          handleEcoArrayKeyDown(e, 'intensidadeProtMitral', i)
                        }
                      />
                    ))}
                    <DeleteButton
                      field="intensidadeProtMitral"
                      onDelete={handleClearArrayAndDelete}
                    />
                  </div>
                  {eco?.refluxoPM?.map((o, index) => (
                    <span key={index} className={styles.spanStyle}>
                      [ {o.CODINTENSIDADE} —{' '}
                      {o.INTENSIDADE || 'Descrição não encontrada'} ]
                    </span>
                  ))}{' '}
                </Col>
                <Col>
                  <Form.Label>Prótese Aórtica</Form.Label>
                  <Form.Control
                    name="gradAortica"
                    style={editStyle}
                    type="number"
                    ref={ecoRefs.current['gradAortica']}
                    value={formData.gradAortica}
                    onChange={handleChange}
                    onKeyDown={e => handleEcoKeyDown(e, 'gradAortica')}
                  />{' '}
                  <Form.Label>Refluxo</Form.Label>
                  <div className="d-flex flex-wrap gap-1">
                    {formData.refluxoProtAortica.map((val, i) => (
                      <Form.Control
                        key={i}
                        ref={ecoArrayRefs.current.refluxoProtAortica?.[i]}
                        size="sm"
                        style={arrayStyle}
                        value={val ?? ''}
                        onChange={e =>
                          handleArrayChange(e, 'refluxoProtAortica', i)
                        }
                        onKeyDown={e =>
                          handleEcoArrayKeyDown(e, 'refluxoProtAortica', i)
                        }
                      />
                    ))}
                    <DeleteButton
                      field="refluxoProtAortica"
                      onDelete={handleClearArrayAndDelete}
                    />
                  </div>
                  {eco?.refluxoPAO?.map((o, index) => (
                    <span key={index} className={styles.spanStyle}>
                      [ {o.CODREFPAO} —{' '}
                      {o.DESCRICAO || 'Descrição não encontrada'} ]
                    </span>
                  ))}{' '}
                  <Form.Label>Intensidade</Form.Label>
                  <div className="d-flex flex-wrap gap-1">
                    {formData.intensidadeProtAortica.map((val, i) => (
                      <Form.Control
                        key={i}
                        ref={ecoArrayRefs.current.intensidadeProtAortica?.[i]}
                        size="sm"
                        style={arrayStyle}
                        value={val ?? ''}
                        onChange={e =>
                          handleArrayChange(e, 'intensidadeProtAortica', i)
                        }
                        onKeyDown={e =>
                          handleEcoArrayKeyDown(e, 'intensidadeProtAortica', i)
                        }
                      />
                    ))}
                    <DeleteButton
                      field="intensidadeProtAortica"
                      onDelete={handleClearArrayAndDelete}
                    />
                  </div>
                  {eco?.refluxoPAO?.map((o, index) => (
                    <span key={index} className={styles.spanStyle}>
                      [ {o.CODINTENSIDADE} —{' '}
                      {o.INTENSIDADE || 'Descrição não encontrada'} ]
                    </span>
                  ))}{' '}
                </Col>
                <Col>
                  <Form.Label>Prótese Tricúspide</Form.Label>
                  <Form.Control
                    name="gradTricuspide"
                    style={editStyle}
                    type="number"
                    ref={ecoRefs.current['gradTricuspide']}
                    value={formData.gradTricuspide}
                    onChange={handleChange}
                    onKeyDown={e => handleEcoKeyDown(e, 'gradTricuspide')}
                  />{' '}
                  <Form.Label>Refluxo</Form.Label>
                  <div className="d-flex flex-wrap gap-1">
                    {formData.refluxoProtTricuspide.map((val, i) => (
                      <Form.Control
                        key={i}
                        ref={ecoArrayRefs.current.refluxoProtTricuspide?.[i]}
                        size="sm"
                        style={arrayStyle}
                        value={val ?? ''}
                        onChange={e =>
                          handleArrayChange(e, 'refluxoProtTricuspide', i)
                        }
                        onKeyDown={e =>
                          handleEcoArrayKeyDown(e, 'refluxoProtTricuspide', i)
                        }
                      />
                    ))}
                    <DeleteButton
                      field="refluxoProtTricuspide"
                      onDelete={handleClearArrayAndDelete}
                    />
                  </div>
                  {eco?.refluxoPT?.map((o, index) => (
                    <span key={index} className={styles.spanStyle}>
                      [ {o.CODREFPT} —{' '}
                      {o.DESCRICAO || 'Descrição não encontrada'} ]
                    </span>
                  ))}{' '}
                  <Form.Label>Intensidade</Form.Label>
                  <div className="d-flex flex-wrap gap-1">
                    {formData.intensidadeProtTricuspide.map((val, i) => (
                      <Form.Control
                        key={i}
                        ref={
                          ecoArrayRefs.current.intensidadeProtTricuspide?.[i]
                        }
                        size="sm"
                        style={arrayStyle}
                        value={val ?? ''}
                        onChange={e =>
                          handleArrayChange(e, 'intensidadeProtTricuspide', i)
                        }
                        onKeyDown={e =>
                          handleEcoArrayKeyDown(
                            e,
                            'intensidadeProtTricuspide',
                            i
                          )
                        }
                      />
                    ))}
                    <DeleteButton
                      field="intensidadeProtTricuspide"
                      onDelete={handleClearArrayAndDelete}
                    />
                  </div>
                  {eco?.refluxoPT?.map((o, index) => (
                    <span key={index} className={styles.spanStyle}>
                      [ {o.CODINTENSIDADE} —{' '}
                      {o.INTENSIDADE || 'Descrição não encontrada'} ]
                    </span>
                  ))}{' '}
                </Col>

                <Col>
                  <Form.Label>Prótese Pulmonar</Form.Label>
                  <Form.Control
                    name="gradPulmonar"
                    style={editStyle}
                    type="number"
                    ref={ecoRefs.current['gradPulmonar']}
                    value={formData.gradPulmonar}
                    onChange={handleChange}
                    onKeyDown={e => handleEcoKeyDown(e, 'gradPulmonar')}
                  />{' '}
                  <Form.Label>Refluxo</Form.Label>
                  <div className="d-flex flex-wrap gap-1">
                    {formData.refluxoProtPulmonar.map((val, i) => (
                      <Form.Control
                        key={i}
                        ref={ecoArrayRefs.current.refluxoProtPulmonar?.[i]}
                        size="sm"
                        style={arrayStyle}
                        value={val ?? ''}
                        onChange={e =>
                          handleArrayChange(e, 'refluxoProtPulmonar', i)
                        }
                        onKeyDown={e =>
                          handleEcoArrayKeyDown(e, 'refluxoProtPulmonar', i)
                        }
                      />
                    ))}
                    <DeleteButton
                      field="refluxoProtPulmonar"
                      onDelete={handleClearArrayAndDelete}
                    />
                  </div>
                  {eco?.refluxoPP?.map((o, index) => (
                    <span key={index} className={styles.spanStyle}>
                      [ {o.CODREFPP} —{' '}
                      {o.DESCRICAO || 'Descrição não encontrada'} ]
                    </span>
                  ))}{' '}
                  <Form.Label>Intensidade</Form.Label>
                  <div className="d-flex flex-wrap gap-1">
                    {formData.intensidadeProtPulmonar.map((val, i) => (
                      <Form.Control
                        key={i}
                        ref={ecoArrayRefs.current.intensidadeProtPulmonar?.[i]}
                        size="sm"
                        style={arrayStyle}
                        value={val ?? ''}
                        onChange={e =>
                          handleArrayChange(e, 'intensidadeProtPulmonar', i)
                        }
                        onKeyDown={e =>
                          handleEcoArrayKeyDown(e, 'intensidadeProtPulmonar', i)
                        }
                      />
                    ))}
                    <DeleteButton
                      field="intensidadeProtPulmonar"
                      onDelete={handleClearArrayAndDelete}
                    />
                  </div>
                  {eco?.refluxoPP?.map((o, index) => (
                    <span key={index} className={styles.spanStyle}>
                      [ {o.CODINTENSIDADE} —{' '}
                      {o.INTENSIDADE || 'Descrição não encontrada'} ]
                    </span>
                  ))}{' '}
                </Col>
              </Row>
            </Form.Group>

            {/* XIII - Conclusões */}
            <Form.Group>
              <Form.Label>XIII - Conclusões</Form.Label>
              <Row>
                <Col xs="auto">
                  <Form.Label>Valvulopatia</Form.Label>
                  <div className="d-flex flex-wrap gap-1">
                    {formData.valvulopatia.map((val, i) => (
                      <Form.Control
                        key={i}
                        ref={ecoArrayRefs.current.valvulopatia?.[i]}
                        size="sm"
                        style={arrayStyle}
                        value={val ?? ''}
                        onChange={e => handleArrayChange(e, 'valvulopatia', i)}
                        onKeyDown={e =>
                          handleEcoArrayKeyDown(e, 'valvulopatia', i)
                        }
                      />
                    ))}
                    <DeleteButton
                      field="valvulopatia"
                      onDelete={handleClearArrayAndDelete}
                    />
                  </div>
                  {eco?.valvulopatia?.map((o, index) => (
                    <span key={index} className={styles.spanStyle}>
                      [ {o.CODVALVO} — {o.VALVO || 'Descrição não encontrada'} ]
                    </span>
                  ))}{' '}
                </Col>
                <Col xs="auto">
                  <Form.Label>Prótese</Form.Label>
                  <div className="d-flex flex-wrap gap-1">
                    {formData.protese.map((val, i) => (
                      <Form.Control
                        key={i}
                        ref={ecoArrayRefs.current.protese?.[i]}
                        size="sm"
                        style={arrayStyle}
                        value={val ?? ''}
                        onChange={e => handleArrayChange(e, 'protese', i)}
                        onKeyDown={e => handleEcoArrayKeyDown(e, 'protese', i)}
                      />
                    ))}
                    <DeleteButton
                      field="protese"
                      onDelete={handleClearArrayAndDelete}
                    />
                  </div>
                  {eco?.protese?.map((o, index) => (
                    <span key={index} className={styles.spanStyle}>
                      [ {o.CODPROTESE} —{' '}
                      {o.PROTESE || 'Descrição não encontrada'} ]
                    </span>
                  ))}{' '}
                </Col>
                <Col xs="auto">
                  <Form.Label>Con Seg.</Form.Label>
                  <div className="d-flex flex-wrap gap-1">
                    {formData.conSeg.map((val, i) => (
                      <Form.Control
                        key={i}
                        ref={ecoArrayRefs.current.conSeg?.[i]}
                        size="sm"
                        style={arrayStyle}
                        value={val ?? ''}
                        onChange={e => handleArrayChange(e, 'conSeg', i)}
                        onKeyDown={e => handleEcoArrayKeyDown(e, 'conSeg', i)}
                      />
                    ))}
                    <DeleteButton
                      field="conSeg"
                      onDelete={handleClearArrayAndDelete}
                    />
                  </div>
                  {eco?.contraSeg?.map((o, index) => (
                    <span key={index} className={styles.spanStyle}>
                      [ {o.CODCONTRASEG} —{' '}
                      {o.DESCRICAO || 'Descrição não encontrada'} ]
                    </span>
                  ))}{' '}
                </Col>
              </Row>
              <Row>
                <Col xs="auto">
                  <Form.Label>Intensidade</Form.Label>
                  <div className="d-flex flex-wrap gap-1">
                    {formData.intensidadeValv.map((val, i) => (
                      <Form.Control
                        key={i}
                        ref={ecoArrayRefs.current.intensidadeValv?.[i]}
                        size="sm"
                        style={arrayStyle}
                        value={val ?? ''}
                        onChange={e =>
                          handleArrayChange(e, 'intensidadeValv', i)
                        }
                        onKeyDown={e =>
                          handleEcoArrayKeyDown(e, 'intensidadeValv', i)
                        }
                      />
                    ))}
                    <DeleteButton
                      field="intensidadeValv"
                      onDelete={handleClearArrayAndDelete}
                    />
                  </div>
                  {eco?.valvulopatia?.map((o, index) => (
                    <span key={index} className={styles.spanStyle}>
                      [ {o.CODINTENSIDADE} —{' '}
                      {o.INTENSIDADE || 'Descrição não encontrada'} ]
                    </span>
                  ))}{' '}
                </Col>

                <Col xs="auto">
                  <Form.Label>Desc.</Form.Label>
                  <div className="d-flex flex-wrap gap-1">
                    {formData.descProtese.map((val, i) => (
                      <Form.Control
                        key={i}
                        ref={ecoArrayRefs.current.descProtese?.[i]}
                        size="sm"
                        style={arrayStyle}
                        value={val ?? ''}
                        onChange={e => handleArrayChange(e, 'descProtese', i)}
                        onKeyDown={e =>
                          handleEcoArrayKeyDown(e, 'descProtese', i)
                        }
                      />
                    ))}
                    <DeleteButton
                      field="descProtese"
                      onDelete={handleClearArrayAndDelete}
                    />
                  </div>
                  {eco?.protese?.map((o, index) => (
                    <span key={index} className={styles.spanStyle}>
                      [ {o.CDODESCRICAO} —{' '}
                      {o.DESCRICAO || 'Descrição não encontrada'} ]
                    </span>
                  ))}{' '}
                </Col>
                <Col xs="auto">
                  <Form.Label>Região</Form.Label>
                  <div className="d-flex flex-wrap gap-1">
                    {formData.regiao.map((val, i) => (
                      <Form.Control
                        key={i}
                        ref={ecoArrayRefs.current.regiao?.[i]}
                        size="sm"
                        style={arrayStyle}
                        value={val ?? ''}
                        onChange={e => handleArrayChange(e, 'regiao', i)}
                        onKeyDown={e => handleEcoArrayKeyDown(e, 'regiao', i)}
                      />
                    ))}
                    <DeleteButton
                      field="regiao"
                      onDelete={handleClearArrayAndDelete}
                    />
                  </div>
                  {eco?.contraSeg?.map((o, index) => (
                    <span key={index} className={styles.spanStyle}>
                      [ {o.CODREGIAO} — {o.REGIAO || 'Descrição não encontrada'}{' '}
                      ]
                    </span>
                  ))}{' '}
                </Col>
              </Row>
              <Row>
                <Col xs="auto">
                  <Form.Label>Predominância</Form.Label>
                  <div className="d-flex flex-wrap gap-1">
                    {formData.predominanciaValv.map((val, i) => (
                      <Form.Control
                        key={i}
                        ref={ecoArrayRefs.current.predominanciaValv?.[i]}
                        size="sm"
                        style={arrayStyle}
                        value={val ?? ''}
                        onChange={e =>
                          handleArrayChange(e, 'predominanciaValv', i)
                        }
                        onKeyDown={e =>
                          handleEcoArrayKeyDown(e, 'predominanciaValv', i)
                        }
                      />
                    ))}
                    <DeleteButton
                      field="predominanciaValv"
                      onDelete={handleClearArrayAndDelete}
                    />
                  </div>
                  {eco?.valvulopatia?.map((o, index) => (
                    <span key={index} className={styles.spanStyle}>
                      [ {o.CODPREDOMI} —{' '}
                      {o.PREDOMI || 'Descrição não encontrada'} ]
                    </span>
                  ))}{' '}
                </Col>
                <Col xs="auto">
                  <Form.Label>Predominância</Form.Label>
                  <div className="d-flex flex-wrap gap-1">
                    {formData.predominanciaProtese.map((val, i) => (
                      <Form.Control
                        key={i}
                        ref={ecoArrayRefs.current.predominanciaProtese?.[i]}
                        size="sm"
                        style={arrayStyle}
                        value={val ?? ''}
                        onChange={e =>
                          handleArrayChange(e, 'predominanciaProtese', i)
                        }
                        onKeyDown={e =>
                          handleEcoArrayKeyDown(e, 'predominanciaProtese', i)
                        }
                      />
                    ))}
                    <DeleteButton
                      field="predominanciaProtese"
                      onDelete={handleClearArrayAndDelete}
                    />
                  </div>
                  {eco?.protese?.map((o, index) => (
                    <span key={index} className={styles.spanStyle}>
                      [ {o.CODPREDOMI} —{' '}
                      {o.PREDOMI || 'Descrição não encontrada'} ]
                    </span>
                  ))}{' '}
                </Col>
                <Col></Col>
              </Row>
              <Row>
                <Col xs="auto">
                  <Form.Label>Conclusões</Form.Label>
                  <div className="d-flex flex-wrap gap-1">
                    {formData.conclusoes.map((val, i) => (
                      <Form.Control
                        key={i}
                        ref={ecoArrayRefs.current.conclusoes?.[i]}
                        size="sm"
                        style={arrayStyle}
                        value={val ?? ''}
                        onChange={e => handleArrayChange(e, 'conclusoes', i)}
                        onKeyDown={e =>
                          handleEcoArrayKeyDown(e, 'conclusoes', i)
                        }
                      />
                    ))}
                    <DeleteButton
                      field="conclusoes"
                      onDelete={handleClearArrayAndDelete}
                    />
                  </div>
                  {eco?.conclusoes?.map((o, index) => (
                    <span key={index} className={styles.spanStyle}>
                      [ {o.CODCONCLU} —{' '}
                      {o.DESCRICAO || 'Descrição não encontrada'} ]
                    </span>
                  ))}{' '}
                </Col>
              </Row>
              <Row>
                <Col xs="auto">
                  <Form.Label>Intensidade</Form.Label>
                  <div className="d-flex flex-wrap gap-1">
                    {formData.intensidadeConclusoes.map((val, i) => (
                      <Form.Control
                        key={i}
                        ref={ecoArrayRefs.current.intensidadeConclusoes?.[i]}
                        size="sm"
                        style={arrayStyle}
                        value={val ?? ''}
                        onChange={e =>
                          handleArrayChange(e, 'intensidadeConclusoes', i)
                        }
                        onKeyDown={e =>
                          handleEcoArrayKeyDown(e, 'intensidadeConclusoes', i)
                        }
                      />
                    ))}
                    <DeleteButton
                      field="intensidadeConclusoes"
                      onDelete={handleClearArrayAndDelete}
                    />
                  </div>
                  {eco?.conclusoes?.map((o, index) => (
                    <span key={index} className={styles.spanStyle}>
                      [ {o.CODINTENSIDADE} —{' '}
                      {o.INTENSIDADE || 'Descrição não encontrada'} ]
                    </span>
                  ))}{' '}
                </Col>
              </Row>
            </Form.Group>
            <Form.Group size="sm" controlId="editDescricao">
              <strong>XIV - OBSERVAÇÕES</strong>
              <Button
                variant="outline-info"
                size="sm"
                className="mb-2 ml-2"
                onClick={() => setShowModal(true)}
              >
                Buscar Observação Anterior
              </Button>
              <Form.Control
                name="observacoes"
                rows="5"
                value={formData.observacoes || ''}
                onChange={handleChange}
                size="sm"
                ref={ecoRefs.current['observacoes']}
                onKeyDown={e => handleEcoKeyDown(e, 'observacoes')}
                as="textarea"
              />
            </Form.Group>

            <ModalEco
              show={showModal}
              onHide={() => setShowModal(false)}
              codPac={eco?.CODPAC}
              onSelect={obs => {
                setFormData(prev => ({ ...prev, observacoes: obs }));
                setShowModal(false);
              }}
              onCopy={ecoSelected => {
                setFormData(prev => ({
                  ...prev,
                  ...mapEcoFormFields(ecoSelected),
                  superficiecorporea: superficieCorporea || eco?.SUPCORP,
                }));
                setShowModal(false);
              }}
            />

            <Card.Footer className="text-center">
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                    />{' '}
                    Salvando...
                  </>
                ) : (
                  'Gravar'
                )}
              </Button>{' '}
              <Button
                variant="secondary"
                onClick={() => {
                  history.push({
                    pathname: `/reportEcoCongenito/${eco?.CODECO}`,
                    state: { printing: false },
                  });
                }}
              >
                Imprimir
              </Button>{' '}
              <Button
                variant="secondary"
                onClick={() => {
                  history.push({
                    pathname: `/reportEcoCongenito/${eco?.CODECO}`,
                    state: { printing: true },
                  });
                }}
              >
                Imprimir Timbrado
              </Button>{' '}
              <Button
                variant="info"
                onClick={() => {
                  if (eco?.CODREALIZOU === 4860) {
                    history.push(`/espelhoCongTxt/${eco?.CODECO}`);
                  } else {
                    history.push(`/espelhoCong/${eco?.CODECO}`);
                  }
                }}
              >
                Espelho
              </Button>{' '}
              <Button
                variant="secondary"
                onClick={() => generateEcoCongenitoPDF(eco, 'imprimirTimbrado')}
              >
                Visualizar PDF
              </Button>{' '}
              <Button
                variant="outline-success"
                disabled={isSubmitting}
                onClick={() => generateEcoCongenitoPDF(eco, 'envioWpp')}
              >
                <FaWhatsapp /> Enviar PDF
              </Button>{' '}
              <Button variant="warning" onClick={() => setShowUpload(true)}>
                Anexar
              </Button>{' '}
              <Button
                variant="warning"
                disabled
                onClick={() => generateDopplerTextReport(eco)}
              >
                Gerar TXT
              </Button>
            </Card.Footer>
          </Form>
        </Card.Body>
      </Card>

      <Modal show={showUpload} onHide={handleCloseModal}>
        <Modal.Header closeButton>
          <Modal.Title>Anexar arquivo</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <CustomFileInput
            id="file-upload"
            label="Selecionar arquivo"
            multiple={true}
            onChange={e => setFiles(Array.from(e.target.files))}
          />
          <ul>
            {files && files.map(({ name }) => <li key={name}>{name}</li>)}
          </ul>
        </Modal.Body>
        <Modal.Footer>
          {isUploading ? (
            <Spinner animation="border" variant="dark" />
          ) : (
            <Button onClick={confirmUpload}>Confirmar</Button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
}
